// src/components/chat/ExpandableDocumentList.tsx
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandToggle from "components/layout/ExpandToggle";
import React, { useState } from "react";
import { useTranslation } from "../../i18n";

export interface SubmittedDocument {
  id: string;
  file_name: string;
  file_url: string;
  download_url?: string;  // EXPORT-scope-gated download endpoint (066 B / option A)
  created_at: string;
  processing_status: string;
}

interface ExpandableDocumentListProps {
  documents: SubmittedDocument[];
  onDocumentClick: (url: string, downloadUrl?: string, fileName?: string) => void;
  onDocumentDelete?: (id: string) => void;
}

const ExpandableDocumentList: React.FC<ExpandableDocumentListProps> = ({
  documents,
  onDocumentClick,
  onDocumentDelete,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Don't render if no documents
  if (documents.length === 0) {
    return null;
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: "transparent",
        borderRadius: 1,
      }}
    >
      {/* Ultra-compact Header */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 0.75,
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Typography 
          variant="body2" 
          fontWeight={600}
          sx={{ 
            color: theme.custom?.contentText?.primary || theme.palette.text.primary,
            letterSpacing: "0.02em",
          }}
        >
          📁 {t("chat.submitted_documents", "Submitted")} ({documents.length})
        </Typography>
        <ExpandToggle expanded={expanded} />
      </Box>

      {/* Expandable List with reduced max height */}
      <Collapse in={expanded}>
        <List
          dense
          disablePadding
          sx={{
            maxHeight: 120,
            overflowY: "auto",
            py: 0,
          }}
        >
          {documents.map((doc) => (
            <ListItem
              key={doc.id}
              disablePadding
              sx={{ py: 0 }}
              secondaryAction={
                  onDocumentDelete && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDocumentDelete(doc.id);
                      }}
                      size="small"
                      sx={{ mr: 0.25, p: 0.5 }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )
              }
            >
              <ListItemButton
                onClick={() =>
                  onDocumentClick(doc.file_url, doc.download_url, doc.file_name)
                }
                sx={{
                  py: 0.25,
                  px: 1.5,
                  minHeight: 28,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <DescriptionIcon sx={{ fontSize: 14 }} color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={doc.file_name || t("chat.untitled_document", "Untitled Document")}
                  secondary={formatDate(doc.created_at)}
                  primaryTypographyProps={{
                    variant: "caption",
                    noWrap: true,
                    sx: { 
                      fontWeight: 500, 
                      fontSize: "0.75rem",
                      color: theme.custom?.contentText?.primary || theme.palette.text.primary,
                    },
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    sx: { 
                      fontSize: "0.65rem",
                      color: theme.custom?.contentText?.secondary || theme.palette.text.secondary,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

export default ExpandableDocumentList;
